/**
 * LanguageSwitcher - Dropdown to change UI language
 *
 * Used on LoginPage (top right) and in Sidebar (footer).
 * Shows current language name; selecting an option updates LanguageContext and persists to localStorage/profile.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from '@/contexts/LanguageContext';
import { Globe } from 'lucide-react';
import type { Language } from '@/types';
import { cn } from '@/lib/utils';

const LANGUAGES: { value: Language; labelKey: string }[] = [
  { value: 'pt-BR', labelKey: 'language.pt-BR' },
  { value: 'en', labelKey: 'language.en' },
  { value: 'es', labelKey: 'language.es' },
];

const LANGUAGE_FLAGS: Record<Language, string> = {
  'pt-BR': '🇧🇷',
  en: '🇺🇸',
  es: '🇪🇸',
};

interface LanguageSwitcherProps {
  variant?: 'compact' | 'full';
  iconVariant?: 'globe' | 'flags';
  className?: string;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ variant = 'full', iconVariant = 'globe', className }) => {
  const { t, language, setLanguage } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const currentLabel = t(LANGUAGES.find((l) => l.value === language)?.labelKey ?? 'language.pt-BR');
  const currentFlag = LANGUAGE_FLAGS[language];

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground hover:bg-muted/50 transition-colors',
          variant === 'compact' && 'px-2 py-1.5'
        )}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={iconVariant === 'flags' ? `${currentLabel} (${language})` : 'Select language'}
      >
        {iconVariant === 'flags' ? (
          <span className="text-lg flex-shrink-0 leading-none" aria-hidden>{currentFlag}</span>
        ) : (
          <Globe className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        )}
        {variant === 'full' && <span className="truncate max-w-[100px]">{currentLabel}</span>}
        <svg
          className={cn('h-3.5 w-3.5 text-muted-foreground flex-shrink-0 transition-transform', open && 'rotate-180')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 z-50 mt-1 min-w-[140px] rounded-lg border border-border bg-popover py-1 shadow-lg"
          style={{ top: '100%' }}
        >
          {LANGUAGES.map((opt) => (
            <li key={opt.value} role="option" aria-selected={language === opt.value}>
              <button
                type="button"
                onClick={() => {
                  setLanguage(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  'w-full px-3 py-2 text-left text-sm transition-colors flex items-center gap-2',
                  language === opt.value
                    ? 'bg-primary/10 text-primary font-medium'
                    : 'text-foreground hover:bg-muted'
                )}
              >
                {language === opt.value && iconVariant !== 'flags' && (
                  <span className="w-2 h-2 rounded-full bg-primary flex-shrink-0" aria-hidden />
                )}
                {iconVariant === 'flags' && (
                  <span className="text-base flex-shrink-0 leading-none" aria-hidden>{LANGUAGE_FLAGS[opt.value]}</span>
                )}
                {t(opt.labelKey)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LanguageSwitcher;
